import { useEffect, useState } from "react";
import { CompanyProvider, useCompany } from "./company/CompanyProvider";
import { NeedCompany } from "./company/NeedCompany";
import { AppShell } from "./components/AppShell";
import { AuthGate } from "./components/AuthGate";
import { MobileShell } from "./mobile/MobileShell";
import { useFxMobile } from "./mobile/useFxMobile";
import { installHubBack, useHubBack } from "./mobile/hubBack";
import { dismissFacturxBootSplash, markFacturxBootSplashBorn } from "./facturxBootSplash";
import { FacturxPwaOnboard } from "./pwa/FacturxPwaOnboard";
import { CatalogPage } from "./pages/CatalogPage";
import { ClientsPage } from "./pages/ClientsPage";
import { DashboardPage } from "./pages/DashboardPage";
import { EinvoicePage } from "./pages/EinvoicePage";
import { CreditsPage, type CreditSeed } from "./pages/CreditsPage";
import { InvoicesPage, type InvoiceSeed } from "./pages/InvoicesPage";
import { RecurringPage, type RecurringSeed } from "./pages/RecurringPage";
import { PaymentsPage } from "./pages/PaymentsPage";
import { ReportsPage } from "./pages/ReportsPage";
import { QuotesPage, type QuoteSeed } from "./pages/QuotesPage";
import { SettingsPage } from "./pages/SettingsPage";
import type { SearchOpen } from "./features/search/types";
import type { AppPage } from "./types";

function FacturxApp() {
  const mobile = useFxMobile();
  const [page, setPage] = useState<AppPage>("dashboard");
  const [clientsGen, setClientsGen] = useState(0);
  const [catalogGen, setCatalogGen] = useState(0);
  const [quotesGen, setQuotesGen] = useState(0);
  const [invoicesGen, setInvoicesGen] = useState(0);
  const [creditsGen, setCreditsGen] = useState(0);
  const [recurringGen, setRecurringGen] = useState(0);
  const [paymentsGen, setPaymentsGen] = useState(0);
  const [payMonth, setPayMonth] = useState(false);
  const [quoteSeed, setQuoteSeed] = useState<QuoteSeed | null>(null);
  const [invoiceSeed, setInvoiceSeed] = useState<InvoiceSeed | null>(null);
  const [creditSeed, setCreditSeed] = useState<CreditSeed | null>(null);
  const [recurringSeed, setRecurringSeed] = useState<RecurringSeed | null>(null);
  const [clientSeed, setClientSeed] = useState<number | null>(null);
  const [catalogSeed, setCatalogSeed] = useState<number | null>(null);
  const { company, loading, error } = useCompany();

  function openSearch(hit: SearchOpen) {
    if (hit.kind === "client") {
      setClientSeed(hit.id);
      setClientsGen((n) => n + 1);
      setPage("clients");
      return;
    }
    if (hit.kind === "quote") {
      setQuoteSeed({ quoteId: hit.id });
      setQuotesGen((n) => n + 1);
      setPage("quotes");
      return;
    }
    if (hit.kind === "invoice") {
      setInvoiceSeed({ quoteId: hit.id });
      setInvoicesGen((n) => n + 1);
      setPage("invoices");
      return;
    }
    if (hit.kind === "credit_note") {
      setCreditSeed({ quoteId: hit.id });
      setCreditsGen((n) => n + 1);
      setPage("credits");
      return;
    }
    if (hit.kind === "recurring") {
      setRecurringSeed({ recurringId: hit.id });
      setRecurringGen((n) => n + 1);
      setPage("recurring");
      return;
    }
    if (hit.kind === "product") {
      setCatalogSeed(hit.id);
      setCatalogGen((n) => n + 1);
      setPage("catalog");
    }
  }

  useHubBack(page === "dashboard" ? 0 : 1, () => setPage("dashboard"));

  useEffect(() => {
    installHubBack();
    markFacturxBootSplashBorn();
    dismissFacturxBootSplash();
  }, []);

  const Shell = mobile ? MobileShell : AppShell;

  return (
    <Shell
      page={page}
      onPage={(next) => {
        if (next === "clients") setClientsGen((n) => n + 1);
        if (next === "catalog") setCatalogGen((n) => n + 1);
        if (next === "quotes") setQuotesGen((n) => n + 1);
        if (next === "invoices") setInvoicesGen((n) => n + 1);
        if (next === "credits") setCreditsGen((n) => n + 1);
        if (next === "recurring") setRecurringGen((n) => n + 1);
        if (next === "payments") {
          setPayMonth(false);
          setPaymentsGen((n) => n + 1);
        }
        setPage(next);
      }}
      onSearchOpen={openSearch}
    >
      {error ? <p className="rounded-lg bg-red-100 px-3 py-2 text-sm text-red-800">{error}</p> : null}
      {loading && !company ? <p className="text-slate-500">Chargement…</p> : null}
      {!loading && !company ? <NeedCompany /> : null}
      {company ? (
        <div
          className="fx-page"
          key={
            page === "clients"
              ? `clients-${clientsGen}`
              : page === "catalog"
                ? `catalog-${catalogGen}`
                : page === "quotes"
                  ? `quotes-${quotesGen}`
                  : page === "invoices"
                    ? `invoices-${invoicesGen}`
                    : page === "credits"
                      ? `credits-${creditsGen}`
                      : page === "recurring"
                        ? `recurring-${recurringGen}`
                        : page === "payments"
                          ? `payments-${paymentsGen}`
                          : page
          }
        >
          {page === "dashboard" ? (
            <DashboardPage
              companyId={company.id}
              onOpen={(t) => {
                if (t.page === "invoices") {
                  setInvoiceSeed(t.id ? { quoteId: t.id } : t.filter ? { filter: t.filter } : null);
                  setInvoicesGen((n) => n + 1);
                  setPage("invoices");
                } else if (t.page === "quotes") {
                  setQuoteSeed(t.id ? { quoteId: t.id } : t.filter ? { filter: t.filter } : null);
                  setQuotesGen((n) => n + 1);
                  setPage("quotes");
                } else if (t.page === "credits") {
                  if (t.id) setCreditSeed({ quoteId: t.id });
                  setCreditsGen((n) => n + 1);
                  setPage("credits");
                } else if (t.page === "recurring") {
                  setRecurringSeed(t.id ? { recurringId: t.id } : t.filter ? { filter: t.filter } : null);
                  setRecurringGen((n) => n + 1);
                  setPage("recurring");
                } else if (t.page === "payments") {
                  setPayMonth(t.filter === "month");
                  setPaymentsGen((n) => n + 1);
                  setPage("payments");
                }
              }}
            />
          ) : null}
          {page === "clients" ? (
            <ClientsPage
              companyId={company.id}
              seedId={clientSeed}
              onSeedUsed={() => setClientSeed(null)}
              onNewQuote={(clientId) => {
                setQuoteSeed({ clientId });
                setQuotesGen((n) => n + 1);
                setPage("quotes");
              }}
              onOpenQuote={(id) => {
                setQuoteSeed({ quoteId: id });
                setQuotesGen((n) => n + 1);
                setPage("quotes");
              }}
              onNewInvoice={(clientId) => {
                setInvoiceSeed({ clientId });
                setInvoicesGen((n) => n + 1);
                setPage("invoices");
              }}
              onOpenInvoice={(id) => {
                setInvoiceSeed({ quoteId: id });
                setInvoicesGen((n) => n + 1);
                setPage("invoices");
              }}
              onNewRecurring={(clientId) => {
                setRecurringSeed({ clientId });
                setRecurringGen((n) => n + 1);
                setPage("recurring");
              }}
              onOpenRecurring={(id) => {
                setRecurringSeed({ recurringId: id });
                setRecurringGen((n) => n + 1);
                setPage("recurring");
              }}
            />
          ) : null}
          {page === "catalog" ? (
            <CatalogPage
              companyId={company.id}
              seedId={catalogSeed}
              onSeedUsed={() => setCatalogSeed(null)}
              onCreateQuote={(productId) => {
                setQuoteSeed({ productId });
                setQuotesGen((n) => n + 1);
                setPage("quotes");
              }}
              onCreateInvoice={(productId) => {
                setInvoiceSeed({ productId });
                setInvoicesGen((n) => n + 1);
                setPage("invoices");
              }}
            />
          ) : null}
          {page === "quotes" ? (
            <QuotesPage
              companyId={company.id}
              seed={quoteSeed}
              onSeedUsed={() => setQuoteSeed(null)}
              onOpenClient={(clientId) => {
                setClientSeed(clientId);
                setClientsGen((n) => n + 1);
                setPage("clients");
              }}
              onOpenInvoice={(id) => {
                setInvoiceSeed({ quoteId: id });
                setInvoicesGen((n) => n + 1);
                setPage("invoices");
              }}
              onOpenRecurring={(fromDocumentId) => {
                setRecurringSeed({ fromDocumentId });
                setRecurringGen((n) => n + 1);
                setPage("recurring");
              }}
            />
          ) : null}
          {page === "invoices" ? (
            <InvoicesPage
              companyId={company.id}
              seed={invoiceSeed}
              onSeedUsed={() => setInvoiceSeed(null)}
              onOpenClient={(clientId) => {
                setClientSeed(clientId);
                setClientsGen((n) => n + 1);
                setPage("clients");
              }}
              onOpenQuote={(id) => {
                setQuoteSeed({ quoteId: id });
                setQuotesGen((n) => n + 1);
                setPage("quotes");
              }}
              onOpenCredit={(id) => {
                setCreditSeed({ quoteId: id });
                setCreditsGen((n) => n + 1);
                setPage("credits");
              }}
              onOpenRecurring={(fromDocumentId) => {
                setRecurringSeed({ fromDocumentId });
                setRecurringGen((n) => n + 1);
                setPage("recurring");
              }}
            />
          ) : null}
          {page === "credits" ? (
            <CreditsPage
              companyId={company.id}
              seed={creditSeed}
              onSeedUsed={() => setCreditSeed(null)}
              onOpenClient={(clientId) => {
                setClientSeed(clientId);
                setClientsGen((n) => n + 1);
                setPage("clients");
              }}
              onOpenInvoice={(id) => {
                setInvoiceSeed({ quoteId: id });
                setInvoicesGen((n) => n + 1);
                setPage("invoices");
              }}
              onOpenQuote={(id) => {
                setQuoteSeed({ quoteId: id });
                setQuotesGen((n) => n + 1);
                setPage("quotes");
              }}
            />
          ) : null}
          {page === "recurring" ? (
            <RecurringPage
              companyId={company.id}
              seed={recurringSeed}
              onSeedUsed={() => setRecurringSeed(null)}
              onOpenClient={(clientId) => {
                setClientSeed(clientId);
                setClientsGen((n) => n + 1);
                setPage("clients");
              }}
              onOpenInvoice={(id) => {
                setInvoiceSeed({ quoteId: id });
                setInvoicesGen((n) => n + 1);
                setPage("invoices");
              }}
            />
          ) : null}
          {page === "payments" ? (
            <PaymentsPage
              companyId={company.id}
              focusMonth={payMonth}
              onOpenClient={(clientId) => {
                setClientSeed(clientId);
                setClientsGen((n) => n + 1);
                setPage("clients");
              }}
              onOpenInvoice={(id) => {
                setInvoiceSeed({ quoteId: id });
                setInvoicesGen((n) => n + 1);
                setPage("invoices");
              }}
            />
          ) : null}
          {page === "reports" ? (
            <ReportsPage
              companyId={company.id}
              onOpenClient={(clientId) => {
                setClientSeed(clientId);
                setClientsGen((n) => n + 1);
                setPage("clients");
              }}
              onOpenInvoice={(id) => {
                setInvoiceSeed({ quoteId: id });
                setInvoicesGen((n) => n + 1);
                setPage("invoices");
              }}
              onOpenCredit={(id) => {
                setCreditSeed({ quoteId: id });
                setCreditsGen((n) => n + 1);
                setPage("credits");
              }}
            />
          ) : null}
          {page === "einvoice" ? <EinvoicePage /> : null}
          {page === "settings" ? <SettingsPage /> : null}
        </div>
      ) : null}
      <FacturxPwaOnboard />
    </Shell>
  );
}

export default function App() {
  return (
    <AuthGate>
      {(userId) => (
        <CompanyProvider userId={userId}>
          <FacturxApp />
        </CompanyProvider>
      )}
    </AuthGate>
  );
}
